import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SLOTS = [
  { id: "top", label: "Top marquee", hint: "Appears between Hero and About" },
  { id: "bottom", label: "Bottom marquee", hint: "Appears between Works and Services" },
];

const EMPTY_TEXT = { type: "text", text: "", image_url: "" };
const EMPTY_IMAGE = { type: "image", text: "", image_url: "" };

function resolveUrl(src) {
  if (!src) return "";
  return src.startsWith("/") ? `${BACKEND}${src}` : src;
}

function ItemRow({ item, idx, onChange, onRemove, onMove, isFirst, isLast }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image too large (10MB max)."); return; }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const r = await api.post("/uploads", data, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ ...item, image_url: r.data.url });
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="border border-neutral-200 bg-white p-3 flex gap-3 items-center" data-testid={`marquee-item-${idx}`}>
      <div className="flex flex-col gap-1">
        <button type="button" onClick={() => onMove(-1)} disabled={isFirst} className="font-body text-xs px-1 disabled:opacity-30" data-testid={`marquee-item-up-${idx}`}>↑</button>
        <button type="button" onClick={() => onMove(1)} disabled={isLast} className="font-body text-xs px-1 disabled:opacity-30" data-testid={`marquee-item-down-${idx}`}>↓</button>
      </div>

      <div className="flex gap-1">
        {["text", "image"].map((t) => (
          <button
            type="button"
            key={t}
            onClick={() => onChange({ ...item, type: t })}
            className={`px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-body border ${item.type === t ? "bg-[#0A0A0A] text-[#F4F4F0] border-[#0A0A0A]" : "border-neutral-300"}`}
            data-testid={`marquee-item-type-${idx}-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      {item.type === "text" ? (
        <input
          value={item.text}
          onChange={(e) => onChange({ ...item, text: e.target.value })}
          placeholder="Text"
          className="flex-1 border border-neutral-200 px-3 py-2 font-body text-sm"
          data-testid={`marquee-item-text-${idx}`}
        />
      ) : (
        <div className="flex-1 flex items-center gap-3">
          {item.image_url && (
            <img src={resolveUrl(item.image_url)} alt="" className="w-12 h-12 object-contain border border-neutral-100 bg-neutral-50" data-testid={`marquee-item-preview-${idx}`} />
          )}
          <input
            value={item.image_url}
            onChange={(e) => onChange({ ...item, image_url: e.target.value })}
            placeholder="Image URL or upload"
            className="flex-1 border border-neutral-200 px-3 py-2 font-body text-sm"
            data-testid={`marquee-item-url-${idx}`}
          />
          <label className="inline-flex items-center px-3 py-2 border border-neutral-300 font-body text-[10px] uppercase tracking-[0.2em] cursor-pointer hover:bg-neutral-50" data-testid={`marquee-item-upload-label-${idx}`}>
            <input type="file" accept="image/*" onChange={upload} disabled={uploading} className="hidden" data-testid={`marquee-item-upload-${idx}`} />
            {uploading ? "…" : "↑ Upload"}
          </label>
        </div>
      )}

      <button type="button" onClick={onRemove} className="font-body text-xs uppercase tracking-[0.2em] text-[#FF3E00] link-line" data-testid={`marquee-item-remove-${idx}`}>
        Remove
      </button>
    </div>
  );
}

function SlotEditor({ slot, data, onSaved }) {
  const [items, setItems] = useState(data.items || []);
  const [italic, setItalic] = useState(!!data.italic);
  const [outline, setOutline] = useState(!!data.outline);
  const [speed, setSpeed] = useState(data.speed ?? 50);
  const [saving, setSaving] = useState(false);

  const updateItem = (idx, next) => setItems((arr) => arr.map((it, i) => i === idx ? next : it));
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const moveItem = (idx, dir) => setItems((arr) => {
    const next = [...arr];
    const swap = next[idx + dir];
    if (!swap) return arr;
    next[idx + dir] = next[idx];
    next[idx] = swap;
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleaned = items
        .map((it) => ({
          type: it.type === "image" ? "image" : "text",
          text: it.text || "",
          image_url: it.image_url || "",
        }))
        .filter((it) => (it.type === "image" ? it.image_url : it.text));
      await api.put(`/marquees/${slot.id}`, { items: cleaned, italic, outline, speed: Number(speed) || 50 });
      toast.success(`${slot.label} saved.`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-neutral-200 p-6 space-y-4" data-testid={`marquee-form-${slot.id}`}>
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <p className="font-display font-bold text-2xl">{slot.label}</p>
        <p className="font-body text-[11px] text-neutral-500">{slot.hint}</p>
      </div>

      <div className="space-y-2">
        {items.map((it, idx) => (
          <ItemRow
            key={idx}
            item={it}
            idx={idx}
            onChange={(next) => updateItem(idx, next)}
            onRemove={() => removeItem(idx)}
            onMove={(dir) => moveItem(idx, dir)}
            isFirst={idx === 0}
            isLast={idx === items.length - 1}
          />
        ))}
        {items.length === 0 && <p className="font-body text-sm text-neutral-500">No items yet.</p>}
      </div>

      <div className="flex gap-3 flex-wrap pt-2">
        <button type="button" onClick={() => setItems((arr) => [...arr, EMPTY_TEXT])} className="px-4 py-2 border border-neutral-300 font-body text-xs uppercase tracking-[0.2em]" data-testid={`marquee-add-text-${slot.id}`}>
          + Add text
        </button>
        <button type="button" onClick={() => setItems((arr) => [...arr, EMPTY_IMAGE])} className="px-4 py-2 border border-neutral-300 font-body text-xs uppercase tracking-[0.2em]" data-testid={`marquee-add-image-${slot.id}`}>
          + Add image
        </button>
      </div>

      <p className="font-body text-[11px] text-neutral-500 pt-1">A star (✺) divider is automatically inserted between items.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end pt-2">
        <label className="flex items-center gap-3 font-body text-sm cursor-pointer">
          <input type="checkbox" checked={italic} onChange={(e) => setItalic(e.target.checked)} data-testid={`marquee-italic-${slot.id}`} />
          Italic / serif (text only)
        </label>
        <label className="flex items-center gap-3 font-body text-sm cursor-pointer">
          <input type="checkbox" checked={outline} onChange={(e) => setOutline(e.target.checked)} data-testid={`marquee-outline-${slot.id}`} />
          Outline (text only)
        </label>
        <div>
          <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Speed ({speed})</label>
          <input type="range" min="10" max="120" value={speed} onChange={(e) => setSpeed(e.target.value)} className="w-full mt-2" data-testid={`marquee-speed-${slot.id}`} />
        </div>
      </div>

      <button
        type="submit"
        disabled={saving}
        className="w-full bg-[#0A0A0A] text-[#F4F4F0] py-4 font-display font-bold uppercase tracking-widest text-sm hover:bg-[#FF3E00] transition-colors disabled:opacity-40"
        data-testid={`marquee-save-${slot.id}`}
      >
        {saving ? "Saving…" : `Save ${slot.label}`}
      </button>
    </form>
  );
}

export default function MarqueeAdmin() {
  const [data, setData] = useState(null);

  const load = () => api.get("/marquees").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data) {
    return <div className="font-body text-sm text-neutral-500" data-testid="marquee-admin-loading">Loading…</div>;
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6" data-testid="marquee-admin">
      {SLOTS.map((s) => (
        <SlotEditor key={s.id} slot={s} data={data[s.id] || {}} onSaved={load} />
      ))}
    </div>
  );
}
